/**
 * Locked Features Registry
 *
 * Definisce le feature che, quando non accessibili, vengono mostrate in
 * sidebar come "locked" (icona 🔒) anziché essere nascoste. Click → porta a
 * /unlock/:featureKey, che renderizza una landing page con preview + CTA
 * (acquista addon oppure upgrade piano).
 *
 * Discovery feature toggle:
 *   import.meta.env.VITE_FEATURE_LOCKED_DISCOVERY === 'false' → disattivato
 *   (default acceso). Vedi injectLockedFeatures.ts.
 */

export type UnlockBy = 'addon' | 'plan';

export interface LockedFeatureMeta {
  /** Chiave usata sia come `menu item.id` sia come `URL param /unlock/:featureKey`. */
  featureKey: string;
  /** Chiave usata in `subscription_plans.enabled_modules` per check org-level (default: featureKey con dash → underscore). */
  moduleKey?: string;
  /** Nome leggibile mostrato nella sidebar e landing. */
  displayName: string;
  /** Sottotitolo / claim breve nella landing. */
  tagline: string;
  /** Bullet points della landing. */
  bullets: string[];
  /** Tipo di unlock: acquista add-on oppure upgrade piano. */
  unlockBy: UnlockBy;
  /** Solo se unlockBy === 'addon' — chiave del gruppo addon (subscription_addons.addon_group). */
  addonGroup?: string;
  /** Solo se unlockBy === 'plan' — nome del piano minimo richiesto (es. 'Matrix'). */
  minPlanName?: string;
  /** Prezzo "from" stringa già formattata (es. "6 €/m + IVA" o "29 €/mese"). */
  fromPriceLabel?: string;
  /** Icona Lucide name (es. 'Mail', 'MessageSquare'). */
  icon?: string;
}

/**
 * Registry. La chiave è il `menu item.id` di menuConfig.ts.
 * Per aggiungere nuove feature, aggiungere qui + (se serve) inserirla in menuConfig.ts.
 */
export const LOCKED_FEATURES_REGISTRY: Record<string, LockedFeatureMeta> = {
  newsletter: {
    featureKey: 'newsletter',
    displayName: 'Newsletter',
    tagline:
      'Crea campagne email professionali, segmenta i clienti e converti i lead in vendite ricorrenti.',
    bullets: [
      'Editor drag-and-drop, niente HTML',
      'Liste segmentate da CRM (deal stage, tag, ultimi acquisti)',
      '5.000 / 25.000 / 100.000 contatti a scelta',
      'Tracking aperture, click e disiscrizioni',
      'Statistiche e A/B test per ottimizzare le conversioni',
    ],
    unlockBy: 'addon',
    addonGroup: 'newsletter',
    fromPriceLabel: 'da 6 €/mese + IVA',
    icon: 'Send',
  },
  inventory: {
    featureKey: 'inventory',
    displayName: 'Magazzino + WooCommerce',
    tagline:
      'Gestisci inventario e sincronizza automaticamente con il tuo e-commerce. Zero errori, zero fogli Excel.',
    bullets: [
      'Anagrafiche prodotto con varianti e attributi',
      'Sync bidirezionale stock con WooCommerce',
      'Ordini, fornitori e movimenti di magazzino',
      'Avvisi soglie scorta minima',
    ],
    unlockBy: 'addon',
    addonGroup: 'warehouse',
    fromPriceLabel: '59 €/mese + IVA',
    icon: 'Package',
  },
  wiki: {
    featureKey: 'wiki',
    displayName: 'Wiki aziendale',
    tagline:
      'Costruisci la knowledge base interna del team: procedure, sapere condiviso, onboarding nuovi colleghi.',
    bullets: [
      'Pagine ricche con embed, codice, immagini',
      'Permessi per spazio e per pagina',
      'Ricerca full-text immediata',
      'Storico revisioni e commenti',
    ],
    unlockBy: 'plan',
    minPlanName: 'Matrix',
    icon: 'BookOpen',
  },
  tickets: {
    featureKey: 'tickets',
    displayName: 'Ticketing & SLA',
    tagline:
      'Gestisci richieste clienti con priorità, SLA e time tracking. Tutto integrato col CRM.',
    bullets: [
      'Code ticket per team',
      'SLA per priorità con timer',
      'Time tracking per cliente / progetto',
      'Risposte automatiche e template',
    ],
    unlockBy: 'plan',
    minPlanName: 'Matrix',
    icon: 'Ticket',
  },
  bookings: {
    featureKey: 'bookings',
    moduleKey: 'booking',
    displayName: 'Prenotazioni appuntamenti',
    tagline:
      'Calendario di prenotazione self-service. I clienti scelgono lo slot, tu ricevi le conferme nel CRM.',
    bullets: [
      'Disponibilità per operatore / servizio',
      'Link pubblico condivisibile',
      'Reminder automatici email / WhatsApp',
      'Integrazione Google Calendar',
    ],
    unlockBy: 'plan',
    minPlanName: 'Matrix',
    icon: 'CalendarCheck',
  },
  'qr-codes': {
    featureKey: 'qr-codes',
    moduleKey: 'qr_codes',
    displayName: 'QR Code dinamici',
    tagline:
      'Genera QR Code intelligenti che redirigono a URL diversi nel tempo, traccia scansioni e converti il fisico in lead digitali.',
    bullets: [
      'URL di destinazione modificabile dopo la stampa',
      'Statistiche per scansione (data, luogo, device)',
      'QR personalizzati con logo e colori brand',
      'Lead form auto-collegato per cattura contatti',
    ],
    unlockBy: 'plan',
    minPlanName: 'Quantum',
    icon: 'QrCode',
  },
  'screen-recordings': {
    featureKey: 'screen-recordings',
    moduleKey: 'screen_recordings',
    displayName: 'Video screen + webcam',
    tagline:
      'Registra schermo + webcam direttamente dal CRM, genera link condivisibile per clienti e collaboratori. Tipo Loom, integrato.',
    bullets: [
      'Registrazione schermo + camera + audio in 1 click',
      'Link pubblico condivisibile (no account richiesto)',
      'Library video organizzata con tag e ricerca',
      'Allegabile a deal, ticket, progetti',
    ],
    unlockBy: 'plan',
    minPlanName: 'Matrix',
    icon: 'Clapperboard',
  },
  surveys: {
    featureKey: 'surveys',
    displayName: 'Sondaggi & NPS',
    tagline:
      'Misura la soddisfazione clienti e raccogli feedback strutturati con sondaggi NPS / CSAT.',
    bullets: [
      'Template NPS, CSAT, custom',
      'Invio via email o link pubblico',
      'Dashboard risposte aggregata',
      'Trigger automatici post-deal o post-ticket',
    ],
    unlockBy: 'plan',
    minPlanName: 'Quantum',
    icon: 'ClipboardList',
  },
};

/**
 * Helper: ritorna true se la discovery di feature locked è attiva.
 * Default attivo (la env var può solo disattivarla).
 */
export function isLockedDiscoveryEnabled(): boolean {
  // Vite sostituisce a build-time SOLO il pattern statico `import.meta.env.VITE_X`.
  // Optional chaining come `import.meta?.env?.X` blocca la sostituzione → undefined a runtime.
  const raw = import.meta.env.VITE_FEATURE_LOCKED_DISCOVERY as string | undefined;
  return raw !== 'false';
}
